import dayjs from '~/timeInit';

import { APP_EXCLUSIVE_START_HOUR, APP_EXCLUSIVE_TIMEZONE } from '~/consts/appExclusive';

import type {
  EligibleAppExclusiveOffer,
  EligibleAppExclusiveOffersResponse,
  ScheduleRecord,
} from '~/services/OffersService';

export type AppExclusiveStatus = 'scheduled' | 'live' | 'ended';

export interface CalendarDay {
  date: string;
  dayOfMonth: number;
  inMonth: boolean;
  isWeekend: boolean;
}

const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_FORMAT = 'YYYY-MM';
const DAYS_PER_WEEK = 7;

// Monday-first weekday index (0 = Monday ... 6 = Sunday).
function mondayIndex(date: dayjs.Dayjs): number {
  return (date.day() + 6) % DAYS_PER_WEEK;
}

function parseMonth(month: string): dayjs.Dayjs {
  return dayjs.utc(`${month}-01`, DATE_FORMAT);
}

export function currentMonth(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).format(MONTH_FORMAT);
}

export function todayDate(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).format(DATE_FORMAT);
}

/** Sydney day after today: today's 6:00am slot has usually started, so tomorrow is the first useful pick. */
export function defaultAddDealDate(now: dayjs.Dayjs = dayjs()): string {
  return now.tz(APP_EXCLUSIVE_TIMEZONE).add(1, 'day').format(DATE_FORMAT);
}

export function shiftMonth(month: string, delta: number): string {
  return parseMonth(month).add(delta, 'month').format(MONTH_FORMAT);
}

export function formatMonthLabel(month: string): string {
  return parseMonth(month).format('MMMM YYYY');
}

/** Whole weeks (Monday to Sunday) covering the month, including leading and trailing days. */
export function buildCalendarDays(month: string): Array<CalendarDay> {
  const first = parseMonth(month);
  const gridStart = first.subtract(mondayIndex(first), 'day');
  const totalDays = Math.ceil((mondayIndex(first) + first.daysInMonth()) / DAYS_PER_WEEK) * DAYS_PER_WEEK;

  return Array.from({ length: totalDays }, (_, index) => {
    const date = gridStart.add(index, 'day');
    return {
      date: date.format(DATE_FORMAT),
      dayOfMonth: date.date(),
      inMonth: date.format(MONTH_FORMAT) === month,
      isWeekend: mondayIndex(date) >= 5,
    };
  });
}

/**
 * Range for the schedules search. With `overlaps` svc-offer returns every row touching the range,
 * including long rows that start before or end after the grid; without it (older svc-offer) only rows
 * fully inside are returned, so the range is padded by a day either side to keep edge slots.
 */
export function getCalendarQueryRange(days: Array<CalendarDay>): { start: string; end: string } {
  const first = dayjs.tz(days[0].date, APP_EXCLUSIVE_TIMEZONE).subtract(1, 'day');
  const last = dayjs.tz(days[days.length - 1].date, APP_EXCLUSIVE_TIMEZONE).add(2, 'day');
  return { start: first.toISOString(), end: last.toISOString() };
}

export function getScheduleStatus(
  schedule: Pick<ScheduleRecord, 'start' | 'end'>,
  now: dayjs.Dayjs = dayjs(),
): AppExclusiveStatus {
  if (now.isBefore(dayjs(schedule.start))) {
    return 'scheduled';
  }
  if (now.isBefore(dayjs(schedule.end))) {
    return 'live';
  }
  return 'ended';
}

/** The 6:00am-to-6:00am Sydney slot for a calendar day. */
export function getDaySlot(date: string): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
  const start = dayjs.tz(date, APP_EXCLUSIVE_TIMEZONE).hour(APP_EXCLUSIVE_START_HOUR);
  return { start, end: start.add(1, 'day') };
}

/**
 * Places each schedule on every visible day whose slot it overlaps, the same rule svc-offer uses to
 * mark a day as taken. svc-offer rejects app_exclusive rows longer than one slot on create and update,
 * but one can still arrive through a path that skips that check (e.g. a staging import); it then shows
 * on each day it blocks, not only on its first day.
 */
export function groupSchedulesByDate(
  schedules: Array<ScheduleRecord>,
  days: Array<Pick<CalendarDay, 'date'>>,
): Map<string, Array<ScheduleRecord>> {
  const sorted = [...schedules].sort((a, b) => a.start.localeCompare(b.start) || a.id - b.id);
  const byDate = new Map<string, Array<ScheduleRecord>>();
  days.forEach(({ date }) => {
    const slot = getDaySlot(date);
    const overlapping = sorted.filter(
      (schedule) => dayjs(schedule.start).isBefore(slot.end) && dayjs(schedule.end).isAfter(slot.start),
    );
    if (overlapping.length) {
      byDate.set(date, overlapping);
    }
  });
  return byDate;
}

/** A schedule spanning more than one daily slot. */
export function isMultiDaySchedule(schedule: Pick<ScheduleRecord, 'start' | 'end'>): boolean {
  return dayjs(schedule.end).diff(dayjs(schedule.start), 'hour', true) > 24;
}

export function formatSlotTime(iso: string): string {
  return dayjs(iso).tz(APP_EXCLUSIVE_TIMEZONE).format('D MMM, h:mma');
}

export function formatSlotTimeOfDay(iso: string): string {
  return dayjs(iso).tz(APP_EXCLUSIVE_TIMEZONE).format('h:mma');
}

// Defensive: other offer shapes (e.g. App.ContentOffer) model location as a plain string.
type OfferLocation = App.Offer['location'] | string;

/**
 * Short location label for an event card. Accommodation offers carry a structured location
 * (`heading`/`subheading`/`description`); tolerate a plain string too.
 */
export function getOfferLocationLabel(location: OfferLocation | undefined): string | undefined {
  if (!location) {
    return undefined;
  }
  if (typeof location === 'string') {
    return location || undefined;
  }
  return [location.heading, location.subheading].filter(Boolean).join(', ') || undefined;
}

/**
 * Why an eligible offer can't take the slot, or `undefined` when it can. svc-offer allows one
 * `app_exclusive` row per offer and region, so an offer that already has one (upcoming or ended)
 * can't get a second; a taken or ended day blocks every offer and is reported once for the day.
 */
export function getOfferBlockReason(offer: EligibleAppExclusiveOffer, now: dayjs.Dayjs = dayjs()): string | undefined {
  if (offer.canSchedule) {
    return undefined;
  }
  const existing = offer.existingAppExclusive;
  if (!existing) {
    return 'This day is not available';
  }
  const existingDay = dayjs(existing.start).tz(APP_EXCLUSIVE_TIMEZONE).format('D MMM YYYY');
  return getScheduleStatus(existing, now) === 'ended'
    ? `Already ran as App Only Flash on ${existingDay}`
    : `Already booked for ${existingDay}`;
}

/** The deal stops selling when the first of its availability and online purchase windows closes. */
export function getOnSaleEnd(offer: Pick<EligibleAppExclusiveOffer, 'availability' | 'onlinePurchase'>): string {
  const availabilityEnd = dayjs(offer.availability.end);
  const onlinePurchaseEnd = dayjs(offer.onlinePurchase.end);
  return (availabilityEnd.isBefore(onlinePurchaseEnd) ? availabilityEnd : onlinePurchaseEnd).toISOString();
}

/** Sydney calendar day (YYYY-MM-DD) whose 6:00am slot a schedule starts in. */
export function getScheduleDate(schedule: Pick<ScheduleRecord, 'start'>): string {
  return dayjs(schedule.start).tz(APP_EXCLUSIVE_TIMEZONE).format(DATE_FORMAT);
}

export function formatDayLabel(date: string): string {
  return dayjs.utc(date, DATE_FORMAT).format('ddd D MMM');
}

/**
 * Why `offerId` can't take the slot in `eligible`, or `undefined` when it can. Rows in `ignoreScheduleIds`
 * are about to move or be removed (a move or a swap), so they neither block the day nor count as the
 * offer's existing App Only Flash row.
 */
export function getSlotBlockReason(
  eligible: EligibleAppExclusiveOffersResponse,
  offerId: string,
  ignoreScheduleIds: Array<number> = [],
  now: dayjs.Dayjs = dayjs(),
): string | undefined {
  if (!now.isBefore(dayjs(eligible.slot.start))) {
    return 'This day has already started';
  }
  const dayTakenBy = eligible.dayTakenBy;
  if (dayTakenBy && !ignoreScheduleIds.includes(dayTakenBy.scheduleId)) {
    return `Day already taken by offer ${dayTakenBy.offerId}`;
  }
  const offer = eligible.offers.find((item) => item.offerId === offerId);
  if (!offer && eligible.total > eligible.offers.length) {
    // The list was cut off by `limit`, so a missing offer may still be eligible.
    return `Could not confirm the offer is on sale in ${eligible.region}: too many offers match its name`;
  }
  if (!offer) {
    return `Not on sale in ${eligible.region} until ${formatSlotTime(eligible.slot.onSaleUntil)}, or still on the homepage`;
  }
  const existing = offer.existingAppExclusive;
  if (existing && !ignoreScheduleIds.includes(existing.scheduleId)) {
    return getOfferBlockReason({ ...offer, canSchedule: false }, now);
  }
  return undefined;
}

/**
 * Tomorrow and the day after with nothing scheduled. Tomorrow's deal sits on the homepage all day today,
 * so an empty day here needs filling soon.
 */
export function getUpcomingEmptyDays(schedules: Array<ScheduleRecord>, today: string): Array<string> {
  const upcoming = [1, 2].map((offset) => ({
    date: dayjs.utc(today, DATE_FORMAT).add(offset, 'day').format(DATE_FORMAT),
  }));
  const byDate = groupSchedulesByDate(schedules, upcoming);
  return upcoming.map(({ date }) => date).filter((date) => !byDate.has(date));
}

/** Query range covering the slots of the two Sydney days after `today` (YYYY-MM-DD). */
export function getUpcomingQueryRange(today: string): { start: string; end: string } {
  const tomorrow = dayjs.utc(today, DATE_FORMAT).add(1, 'day').format(DATE_FORMAT);
  const start = getDaySlot(tomorrow).start;
  return { start: start.toISOString(), end: start.add(2, 'day').toISOString() };
}
